'use client';

/**
 * Shared presentational primitives for the trial-activation gate (LF-4076).
 *
 * Promoted from the LF-3933 Storybook prototypes so production and the
 * prototype variants render from one source. Purely presentational — the
 * real Stripe round-trip is driven by the gate / landing, not here.
 */

import { signOut } from 'next-auth/react';
import { ArrowRight, CheckCircle2, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * idle      → waiting for the user to click "Activate Trial"
 * redirecting → creating the Stripe session / navigating to checkout
 * activated → returned from Stripe; trial confirmed, handing to the app
 */
export type ActivationState = 'idle' | 'redirecting' | 'activated';

interface ActivateTrialButtonProps {
  state: ActivationState;
  onActivate: () => void;
  label?: string;
  className?: string;
}

/** The fundamental action — identical wording and behaviour everywhere */
export function ActivateTrialButton({
  state,
  onActivate,
  label = 'Activate Trial',
  className
}: ActivateTrialButtonProps) {
  return (
    <Button
      size="lg"
      className={cn('h-11 gap-2 text-sm', className)}
      onClick={onActivate}
      disabled={state !== 'idle'}
    >
      {state === 'redirecting' ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Opening secure checkout…
        </>
      ) : (
        <>
          {label}
          <ArrowRight className="h-4 w-4" />
        </>
      )}
    </Button>
  );
}

/** Trust microcopy that always travels with the CTA */
export function StripeReassurance({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-gray-400',
        className
      )}
    >
      <span className="flex items-center gap-1">
        <Lock className="h-3 w-3" />
        Secure checkout by Stripe
      </span>
      <span aria-hidden="true">·</span>
      <span>No charge today</span>
      <span aria-hidden="true">·</span>
      <span>Cancel anytime</span>
    </div>
  );
}

/**
 * The return from Stripe — the firm has just converted and is about to be
 * handed to the app. Shown briefly while the gate polls for the status to flip.
 *
 * `variant` adapts the copy to the conversion path (LF-4535):
 *   • `trial` (default) — a `pending` firm that started a free trial.
 *   • `subscription` — an `active-free` firm whose card-add converted it to a
 *     paid subscription immediately (no free trial), per the LF-4086 opt-in path.
 */
export function ActivatedPanel({
  firmName,
  variant = 'trial'
}: {
  firmName: string;
  variant?: 'trial' | 'subscription';
}) {
  const isSubscription = variant === 'subscription';
  return (
    <div className="flex flex-col items-center text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
        <CheckCircle2 className="h-6 w-6 text-emerald-600" />
      </div>
      <h2 className="mt-4 text-xl font-semibold tracking-tight text-gray-900">
        {isSubscription ? "You're all set" : 'Your trial is active'}
      </h2>
      <p className="mt-2 max-w-xs text-sm leading-relaxed text-gray-500">
        Welcome to Vale, {firmName}.{' '}
        {isSubscription
          ? 'Your subscription is active and your whole team now has access.'
          : 'Your free trial has started and your whole team now has access.'}
      </p>
      <p className="mt-6 flex items-center gap-2 text-sm text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        {isSubscription
          ? 'Taking you to your dashboard…'
          : 'Taking you to your firm setup…'}
      </p>
    </div>
  );
}

/** A blocked page must always offer an escape hatch */
export function SignedInFooter({
  email,
  className
}: {
  email: string;
  className?: string;
}) {
  return (
    <p className={cn('text-xs text-gray-400', className)}>
      Signed in as {email} ·{' '}
      <button
        type="button"
        className="underline underline-offset-2 hover:text-gray-600"
        onClick={() => signOut({ callbackUrl: '/login' })}
      >
        Sign out
      </button>
    </p>
  );
}
